/**
 * Auth utility functions
 */
import axios from 'axios'
import { setSentryUser, clearSentryUser } from '../sentry'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api'

// Token storage keys (must match api.ts)
const TOKEN_KEY = 'token'
const REFRESH_TOKEN_KEY = 'refreshToken'

// Guard against duplicate exchange calls (React StrictMode runs effects twice)
let exchangeInFlight: Promise<string | null> | null = null

// Exchange OAuth one-time code for tokens, or handle legacy token-in-URL flow
// This is used for OAuth callback (e.g., Google Sign-In)
export const handleTokenFromUrl = async () => {
  const urlParams = new URLSearchParams(window.location.search)

  // New secure flow: exchange one-time code for tokens via POST
  const code = urlParams.get('code')
  if (code) {
    // Prevent duplicate exchange — return existing promise if one is in flight
    if (exchangeInFlight) return exchangeInFlight

    exchangeInFlight = (async () => {
      try {
        // Remove code from URL immediately to prevent re-use on refresh
        const cleanParams = new URLSearchParams()
        urlParams.forEach((value, key) => {
          if (key !== 'code') cleanParams.set(key, value)
        })
        const cleanSearch = cleanParams.toString()
        const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '')
        window.history.replaceState({}, document.title, cleanUrl)

        const response = await axios.post(`${API_BASE_URL}/auth/google/exchange-code`, { code })
        const data = response.data

        if (data.token || data.accessToken) {
          const accessToken = data.accessToken || data.token
          localStorage.setItem(TOKEN_KEY, accessToken)
          if (data.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
          }
          if (data.user) {
            localStorage.setItem('user', JSON.stringify(data.user))
          }

          return accessToken
        }
        return null
      } catch (err) {
        console.error('Failed to exchange OAuth code:', err)
        // Code expired or invalid — redirect to login
        window.location.href = '/login?error=oauth_expired'
        return null
      } finally {
        exchangeInFlight = null
      }
    })()

    return exchangeInFlight
  }

  // Legacy fallback: token directly in URL (will be removed in future)
  const token = urlParams.get('token')
  const refreshToken = urlParams.get('refreshToken')

  if (token) {
    localStorage.setItem(TOKEN_KEY, token)

    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    }

    // Clean up URL (remove token params but keep others like 'role')
    const cleanParams = new URLSearchParams()
    urlParams.forEach((value, key) => {
      if (key !== 'token' && key !== 'refreshToken') {
        cleanParams.set(key, value)
      }
    })
    const cleanSearch = cleanParams.toString()
    const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '')
    window.history.replaceState({}, document.title, cleanUrl)

    // Fetch and store user info if not already present
    if (!localStorage.getItem('user')) {
      await fetchAndStoreUser(token)
    }

    return token
  }

  return null
}

// Fetch current user info from /auth/me and store in localStorage
const fetchAndStoreUser = async (token?: string) => {
  try {
    const authToken = token || localStorage.getItem(TOKEN_KEY)
    if (!authToken) return null

    const response = await axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${authToken}` }
    })

    const user = response.data?.data || response.data
    if (user && user.id) {
      localStorage.setItem('user', JSON.stringify(user))
      if (user.company_id) {
        localStorage.setItem('company_id', String(user.company_id))
      }
      if (user.company_name) {
        localStorage.setItem('company_name', user.company_name)
      }
      // Surface the click-wrap acceptance gap to TermsGate. Empty array means
      // up-to-date; populated array means modal must be shown post-login
      // (covers Google OAuth, refresh, and version-bump scenarios).
      const gap = Array.isArray(user.requires_terms_acceptance)
        ? user.requires_terms_acceptance.filter((t: unknown) => typeof t === 'string')
        : []
      if (gap.length > 0) {
        localStorage.setItem('pending_agreements', JSON.stringify(gap))
      } else {
        localStorage.removeItem('pending_agreements')
      }
      setSentryUser(user)
      window.dispatchEvent(new Event('user-updated'))
      return user
    }
  } catch (err) {
    console.error('Failed to fetch user info:', err)
  }
  return null
}

// Refresh user info from /auth/me on app init so localStorage.user stays in sync
// with DB (company_name, role, etc. can change after login).
export const ensureUserInfo = async () => {
  if (localStorage.getItem(TOKEN_KEY)) {
    await fetchAndStoreUser()
  }
}

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = localStorage.getItem(TOKEN_KEY)
  return !!token
}

// Get stored access token
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY)
}

// Get stored refresh token
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

// Clear authentication (use authAPI.logout() instead for proper server-side logout)
export const logout = () => {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem('user')
  localStorage.removeItem('pending_agreements')
  clearSentryUser()
  window.location.href = '/login'
}
